import type { JSX } from 'react';
import { useState } from 'react';
import type { PlanCode } from '@crv/contracts/tenancy';
import { CreateCompanyRequestSchema, planCodeValues } from '@crv/contracts/tenancy';
import {
  Alert,
  Button,
  Card,
  CardContent,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { NavLink, useNavigate } from 'react-router-dom';
import { getSystemTimeZone, listSupportedTimeZones } from '@crv/shared';

import { createCompany } from '../api/companies';
import { ApiClientError } from '../api/http-client';
import { useAuth } from '../auth/AuthProvider';
import { formatPlanLabel } from '../billing/planBillingLabels';
import { useTenancyStore } from '../tenancy/tenancy-store';

type CreateCompanyFieldErrors = Partial<Record<'name' | 'planCode' | 'timezone', string>>;
type ChangeEventLike = { target: { value?: unknown } };

const timeZoneOptions = listSupportedTimeZones();

export function CreateCompanyPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const auth = useAuth();
  const setSelectedCompanyId = useTenancyStore((state) => state.setSelectedCompanyId);

  const [name, setName] = useState('');
  const [planCode, setPlanCode] = useState<PlanCode>(planCodeValues[0]);
  const [timezone, setTimezone] = useState(getSystemTimeZone());
  const [fieldErrors, setFieldErrors] = useState<CreateCompanyFieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [formErrorDetails, setFormErrorDetails] = useState<string | null>(null);
  const [showErrorDiagnostics, setShowErrorDiagnostics] = useState(false);

  const mutation = useMutation({
    mutationFn: createCompany,
    onSuccess: async (response) => {
      setSelectedCompanyId(response.company.id);
      if (auth.selectCompany) {
        await auth.selectCompany(response.company.id);
      }
      await queryClient.invalidateQueries({ queryKey: ['auth', 'bootstrap'] });
      navigate('/employees', { replace: true });
    },
    onError: (error) => {
      setFormErrorDetails(null);
      setShowErrorDiagnostics(false);
      const message =
        error instanceof ApiClientError
          ? (error.body?.message ?? error.message)
          : error instanceof Error
            ? error.message
            : 'Unable to create company. Please try again.';
      setFormError(message);

      if (error instanceof ApiClientError && error.body) {
        const detailParts: string[] = [`Code: ${error.body.code}`];
        if (error.body.traceId && error.body.traceId !== 'unknown') {
          detailParts.push(`Trace: ${error.body.traceId}`);
        }
        setFormErrorDetails(detailParts.join(' | '));
        console.error('[create-company] API error', {
          status: error.status,
          code: error.body.code,
          traceId: error.body.traceId,
        });
      } else {
        console.error('[create-company] Unexpected error', error);
      }
    },
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setFormError(null);
    setFormErrorDetails(null);
    setShowErrorDiagnostics(false);
    setFieldErrors({});

    const parsed = CreateCompanyRequestSchema.safeParse({
      name,
      planCode,
      timezone: timezone.trim() || undefined,
    });
    if (!parsed.success) {
      const errors: CreateCompanyFieldErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0];
        if ((field === 'name' || field === 'planCode' || field === 'timezone') && !errors[field]) {
          errors[field] = issue.message;
        }
      }
      setFieldErrors(errors);
      return;
    }

    mutation.mutate(parsed.data);
  };

  return (
    <Stack spacing={2.5}>
      <Stack spacing={0.5}>
        <Typography variant="h4">Create company</Typography>
        <Typography variant="body2" color="text.secondary">
          Create your first company or add another company to this account. Choose a plan and create
          your company in the cloud.
        </Typography>
      </Stack>

      <Card variant="outlined" sx={{ maxWidth: 720 }}>
        <CardContent>
          <form onSubmit={handleSubmit} noValidate>
            <Stack spacing={2.5}>
              <TextField
                label="Company name"
                value={name}
                onChange={(event: ChangeEventLike) => {
                  const nextValue = event.target.value;
                  setName(typeof nextValue === 'string' ? nextValue : '');
                }}
                required
                autoFocus
                fullWidth
                error={Boolean(fieldErrors.name)}
                helperText={fieldErrors.name ?? ' '}
              />

              <TextField
                select
                label="Plan"
                value={planCode}
                onChange={(event: ChangeEventLike) => {
                  const nextValue = event.target.value;
                  setPlanCode((typeof nextValue === 'string' ? nextValue : '') as PlanCode);
                }}
                required
                fullWidth
                error={Boolean(fieldErrors.planCode)}
                helperText={fieldErrors.planCode ?? ' '}
              >
                {planCodeValues.map((value) => (
                  <MenuItem key={value} value={value}>
                    {formatPlanLabel(value)}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Timezone"
                value={timezone}
                onChange={(event: ChangeEventLike) => {
                  const nextValue = event.target.value;
                  setTimezone(typeof nextValue === 'string' ? nextValue : '');
                }}
                fullWidth
                error={Boolean(fieldErrors.timezone)}
                helperText={fieldErrors.timezone ?? ' '}
              >
                <MenuItem value="">None</MenuItem>
                {timeZoneOptions.map((timeZoneValue) => (
                  <MenuItem key={timeZoneValue} value={timeZoneValue}>
                    {timeZoneValue}
                  </MenuItem>
                ))}
              </TextField>

              {formError && (
                <Alert severity="error">
                  <Stack spacing={0.5}>
                    <Typography variant="body2">{formError}</Typography>
                    {formErrorDetails && (
                      <Stack spacing={0.5} alignItems="flex-start">
                        <Button
                          type="button"
                          size="small"
                          variant="text"
                          color="inherit"
                          aria-expanded={showErrorDiagnostics}
                          aria-controls="create-company-support-diagnostics"
                          onClick={() => {
                            setShowErrorDiagnostics((current) => !current);
                          }}
                        >
                          {showErrorDiagnostics
                            ? 'Hide support diagnostics'
                            : 'Support diagnostics'}
                        </Button>
                        {showErrorDiagnostics && (
                          <Typography
                            id="create-company-support-diagnostics"
                            variant="caption"
                            color="text.secondary"
                          >
                            {formErrorDetails}
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Stack>
                </Alert>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button type="submit" variant="contained" disabled={mutation.isPending}>
                  {mutation.isPending ? 'Creating...' : 'Create company'}
                </Button>
                <Button component={NavLink} to="/select-company" variant="outlined">
                  Back to selection
                </Button>
              </Stack>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Stack>
  );
}
